/**
 * keys — canonical paths and `where` matching for the merge engine.
 *
 * A canonical path names a node in a model by **key, never by index**:
 *
 *     contactList.tables.contact.columns[id=name].width
 *
 * Index paths would invent conflicts. Two layers that each insert a different
 * column at the front would both "write" `columns[0]`, though they conflict
 * with nothing. A key path makes a conflict mean what it should: two layers
 * writing the same property of the same logical element (30.03.02 §4.6). An
 * unkeyed array falls back to `[#3]`, since there is nothing better.
 *
 * Canonical paths are compared as strings and never parsed back, so a key value
 * containing `.` or `]` is harmless here. Array-key *declarations* are matched
 * against a separate shape path (`columns[]`), never against these, for exactly
 * that reason.
 *
 * Deliberately not `common/utils/object/path.js`: that grammar is the rule
 * engine's, where `[]` means flatten and `||` means fallback. Patch paths are
 * plain dots plus `[field=value]`, and must not quietly acquire either.
 */

import { keyFieldOf } from './arrayKeys.js'
import { isPlainObject, isScalar } from './util.js'

/** How a key value prints inside `[field=value]`. Composite keys print as JSON. */
export const formatKeyValue = (v) => (Array.isArray(v) ? JSON.stringify(v) : String(v))

/** `parent.key`, or `key` at the root. */
export const joinKey = (parent, key) => (parent ? `${parent}.${key}` : String(key))

/** Join segments into a canonical path; `[...]` segments attach without a dot. */
export function canonicalPath(segments) {
    let out = ''
    for (const s of segments) {
        const seg = String(s)
        if (seg.startsWith('[')) out += seg
        else out = out ? `${out}.${seg}` : seg
    }
    return out
}

/** The value of the field that keys this element, or undefined. */
export function keyValueOf(el, keyField) {
    if (keyField === null || keyField === undefined) return undefined
    const f = keyFieldOf(el, keyField)
    if (f === null) return undefined
    const v = el[f]
    return isPlainObject(v) ? undefined : v
}

/**
 * The element's identity under `keyField`: `field=value`. Two elements with the
 * same identity collide. The field name is part of it, so under a multi-field
 * declaration `{json:'a'}` and `{jsonGin:'a'}` are different indexes.
 */
export function identityOf(el, keyField) {
    if (keyField === null || keyField === undefined) return undefined
    const f = keyFieldOf(el, keyField)
    if (f === null || isPlainObject(el[f])) return undefined
    return `${f}=${formatKeyValue(el[f])}`
}

/** The `[field=value]` segment for an element, or `[#index]` when unkeyed. */
export function elementSegment(el, keyField, index) {
    const id = identityOf(el, keyField)
    return id === undefined ? `[#${index}]` : `[${id}]`
}

/** The segment a diagnostic names for a `where` that may have matched nothing. */
export function whereSegment(where, keyField) {
    if (typeof where === 'string') {
        const f = keyField === null || keyField === undefined
            ? '?'
            : Array.isArray(keyField) ? keyField.join('|') : keyField
        return `[${f}=${where}]`
    }
    if (isPlainObject(where)) {
        return `[${Object.keys(where).map(k => `${k}=${formatKeyValue(where[k])}`).join(',')}]`
    }
    return '[?]'
}

/** True when `path` is `ancestor` itself or a node inside it. */
export function isWithin(path, ancestor) {
    if (!ancestor || path === ancestor) return true
    if (!path.startsWith(ancestor)) return false
    const next = path[ancestor.length]
    return next === '.' || next === '['
}

/** True when one path is inside the other — the two writes touch the same data. */
export const overlaps = (a, b) => isWithin(a, b) || isWithin(b, a)

/**
 * A usable `where`: a non-empty string, or a non-empty object whose expected
 * values are all JSON scalars. Matching on a nested object is not supported
 * and does not need to be (30.03.02 §4.3).
 */
export function isValidWhere(where) {
    if (typeof where === 'string') return where.length > 0
    if (!isPlainObject(where)) return false
    const keys = Object.keys(where)
    return keys.length > 0 && keys.every(k => isScalar(where[k]))
}

/**
 * A bare-string `where` is shorthand for `{ [keyField]: where }`. A numeric key
 * — react-grid-layout's `i`, say — matches the string of its number, because the
 * shorthand is a string by construction.
 */
function stringMatches(actual, expected) {
    return actual === expected || (typeof actual === 'number' && String(actual) === expected)
}

/** Does `el` satisfy `where`? An object `where` is an AND of `===` comparisons. */
export function matchesWhere(el, where, keyField) {
    if (typeof where === 'string') {
        const v = keyValueOf(el, keyField)
        return v !== undefined && !Array.isArray(v) && stringMatches(v, where)
    }
    if (!isPlainObject(el)) return false
    for (const k of Object.keys(where)) {
        if (el[k] !== where[k]) return false
    }
    return true
}

/** First matching index and how many elements matched. */
export function matchWhere(array, where, keyField) {
    let index = -1
    let count = 0
    for (let i = 0; i < array.length; i++) {
        if (matchesWhere(array[i], where, keyField)) {
            if (count === 0) index = i
            count++
        }
    }
    return { index, count }
}
